import { localDb } from '@/data/local/database'
import { putLocalEntity, softDeleteLocalEntity } from '@/data/local/mutations'
import { getActiveIdentity } from '@/services/identity/local-identity'

const MIGRATION_KEY = 'anshang.migration.removeMistakeTag.v2.7.5'
const TREE_PREFIX = 'mindCanvas.v2.tree.'

function removeMistakeTagNodes(tree: unknown, moduleName: string) {
  if (moduleName === '错题记录' || !Array.isArray(tree)) return tree
  const nodes = tree.filter(item => item && typeof item === 'object') as Array<Record<string, unknown>>
  const removed = new Set(nodes
    .filter(node => !node.noteId && node.title === '错题记录' && !String(node.content || '').replace(/<p><\/p>/g, '').trim())
    .map(node => String(node.id)))
  if (!removed.size) return tree
  const parentById = new Map(nodes.map(node => [String(node.id), String(node.parentId || 'root')]))
  const survivingParent = (parentId: string) => {
    let current = parentId
    while (removed.has(current)) current = parentById.get(current) || 'root'
    return current
  }
  return nodes
    .filter(node => !removed.has(String(node.id)))
    .map(node => ({ ...node, parentId: survivingParent(String(node.parentId || 'root')) }))
}

function cleanStoredCanvasTrees() {
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index)
    if (!key?.startsWith(TREE_PREFIX)) continue
    const moduleName = key.slice(TREE_PREFIX.length)
    try {
      const current = JSON.parse(localStorage.getItem(key) || '[]')
      const next = removeMistakeTagNodes(current, moduleName)
      if (JSON.stringify(next) !== JSON.stringify(current)) {
        localStorage.setItem(key, JSON.stringify(next))
        localStorage.removeItem(`mindCanvas.v2.treeHash.${moduleName}`)
        localStorage.removeItem(`mindCanvas.v2.savedAt.${moduleName}`)
      }
    } catch { /* 损坏的旧快照交给画布自身回退 */ }
  }
}

/**
 * v2.7.5 数据清理：移除其他一级分类卡片上冗余的「错题记录」标签、
 * 标签注册表记录和思维画布标签节点。
 *
 * 背景：v2.3.9 起错题身份由 category/recordKind 判定（type==='mistake'），
 * 不再依赖「错题记录」标签；但 PDF 导入等旧路径仍强制给卡片附加该标签，
 * 造成「资料分析、判断推理」等模块卡片上残留「错题记录」标签。
 * 本次将全部卡片（含错题记录分类下）的「错题记录」标签移除：
 * - 错题判定：category==='错题记录' || type==='mistake'（不依赖标签）
 * - 模块归属：tags 里的模块名标签（保留，分部分复习仍靠它筛选）
 *
 * 通过 putLocalEntity 写入 change_log，随下次同步推送到服务器与其他设备。
 */
export async function migrateRemoveMistakeTag() {
  try {
    if (localStorage.getItem(MIGRATION_KEY) === 'done') return
    const identity = await getActiveIdentity()
    const notes = await localDb.notes.toArray()
    let migrated = 0
    for (const note of notes) {
      if (note.workspaceId !== identity.workspaceId || note.deletedAtMs) continue
      const currentTags = Array.isArray(note.tags) ? note.tags : []
      if (!currentTags.includes('错题记录')) continue
      const nextTags = currentTags.filter(tag => tag !== '错题记录')
      await putLocalEntity(
        localDb.notes,
        'note',
        { ...note, tags: nextTags, updatedAtMs: Date.now() },
        'update',
        note.version,
      )
      migrated += 1
    }
    const redundantTags = (await localDb.inspirationTags.toArray())
      .filter(tag => tag.workspaceId === identity.workspaceId && !tag.deletedAtMs && tag.name === '错题记录')
    for (const tag of redundantTags) {
      await softDeleteLocalEntity(localDb.inspirationTags, 'inspiration_tag', tag.id)
    }
    cleanStoredCanvasTrees()
    localStorage.setItem(MIGRATION_KEY, 'done')
    if (migrated > 0 || redundantTags.length > 0) {
      console.info(`[migration] 已清理 ${migrated} 张卡片和 ${redundantTags.length} 个重复「错题记录」标签`)
    }
  } catch (error) {
    // 迁移失败不阻塞启动，下次启动重试（标记只在成功后写入）
    console.warn('[migration] removeMistakeTag 跳过：', error)
  }
}
